import { EmptyState } from '@synoptic/ui-kit/empty-state/empty-state.js';
import { TrashIcon } from '@synoptic/ui-kit/icons/react/trash.js';
import { useNavigate } from 'react-router';
import { TournamentPostCardFragmentFragment } from '@/__generated__/graphql';
import { PredictionTournamentPostCardContent } from '@/components/tournament-post-card/prediction-tournament-post-card-content.tsx';
import {
  TournamentType,
  determinateTournamentType,
} from '@/components/tournaments/determinate-tournament-type';
import { paths } from '@/routes-utils/paths';
import { handlePostCardClick } from '@/utils/handle-post-card-click';
import { formatDate } from '../../date-utils/format-date';
import { formatDateWithMs } from '../../date-utils/format-date-with-ms';
import { LayoutCard } from '../layout/card';
import { PostCardActionsMenu } from '../post-card/post-card-actions/post-card-actions-menu';
import { PostCardShareActionsMenu } from '../post-card/post-card-actions/post-card-share-actions-menu';
import { PostCardHeader } from '../post-card/post-card-header';
import { TournamentLabel } from '../post-label/tournament-label';
import { TournamentPostCardContent } from './tournament-post-card-content';
import {
  postCardActions,
  postCardContainer,
  postCardContent,
  postCardDate,
  postCardDeletedPlaceholder,
  postCardFooter,
  postCardLabels,
} from '../post-card/post-card.css';

type TournamentPostCardProps = {
  post: TournamentPostCardFragmentFragment;
  linkToPost?: boolean;
};
export const TournamentPostCard = ({
  post,
  linkToPost,
}: TournamentPostCardProps) => {
  const tournamentType = determinateTournamentType(post.tournament.fields);

  const navigate = useNavigate();

  const isDeleted = Boolean(post.deletedAt);

  return (
    <LayoutCard
      className={postCardContainer}
      hoverable={linkToPost}
      data-deleted={isDeleted ? '' : undefined}
      onClick={(e) => {
        handlePostCardClick({
          event: e,
          linkToPost,
          navigate,
          postPath: paths.makePost(post.id),
        });
      }}
      role={linkToPost ? 'link' : undefined}
      tabIndex={linkToPost ? 0 : undefined}
    >
      <PostCardHeader
        author={post.author}
        actions={<PostCardActionsMenu author={post.author} id={post.id} />}
      />

      <div className={postCardContent}>
        {isDeleted ? (
          <EmptyState
            illustration={<TrashIcon />}
            heading="The post was deleted"
            className={postCardDeletedPlaceholder}
          />
        ) : tournamentType === TournamentType.Prediction ? (
          <PredictionTournamentPostCardContent post={post} />
        ) : (
          <TournamentPostCardContent post={post} />
        )}

        <time
          title={formatDateWithMs(new Date(post.createdAt))}
          dateTime={post.createdAt}
          className={postCardDate}
        >
          {formatDate(new Date(post.createdAt))}
        </time>
      </div>

      <div className={postCardFooter}>
        <div className={postCardLabels} onClick={(e) => e.stopPropagation()}>
          <TournamentLabel
            label={post.tournament.label}
            tournamentId={post.tournament.id}
            linkToTournament
          />
        </div>
        <div className={postCardActions} onClick={(e) => e.stopPropagation()}>
          <PostCardShareActionsMenu post={post} />
        </div>
      </div>
    </LayoutCard>
  );
};
